import { useState, useCallback, useMemo } from "react";
import _debounce from "lodash/debounce";
import _throttle from "lodash/throttle";

// A counter to hang a React `key` on, so a subtree can be told to remount.
//
// Used where a form's SCHEMA changes underneath it rather than its values: the
// Values tab compiles its schema out of `types`, and editing a type has to
// rebuild the whole widget tree. Changing the key is the only honest way to say
// that — the form has no way to diff two schemas.
//
// The throttled and debounced variants exist because the other caller is the
// live preview, which is remounted on every change to the page. Debounced so a
// burst of keystrokes is one reload rather than thirty.
export const useUpdateKey = (wait = 500) => {
  const [key, setKey] = useState(0);

  const update = useCallback(() => setKey((current) => current + 1), []);

  // Memoised on `update`, which is stable — without this each render would mint
  // a new debounced function with its own timer, and every one of them would
  // fire. That is the failure the archived project's version had: the deps
  // array was missing on the throttled half, so it re-throttled per render and
  // stopped throttling anything.
  const throtUpdate = useMemo(() => _throttle(update, wait), [update, wait]);
  const debounceUpdate = useMemo(() => _debounce(update, wait), [update, wait]);

  return { key, update, throtUpdate, debounceUpdate };
};
